import { useEffect, useState } from 'react';
import { Trash2, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORIES = [
  ['ambulances', 'Ambulances'],
  ['vsl', 'VSL'],
  ['taxis', 'Taxis'],
  ['bariatrique', 'Bariatrique'],
  ['equipes', 'Équipes'],
  ['implantations', 'Implantations'],
  ['materiel', 'Matériel'],
];

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ url: '', title: '', category: 'ambulances', order: 0 });
  const [activeTab, setActiveTab] = useState('ambulances');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/gallery');
      setItems(r.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/gallery', form);
      toast.success('Photo ajoutée');
      setOpenCreate(false);
      setForm({ url: '', title: '', category: activeTab, order: 0 });
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setItems(it => it.filter(x => x.id !== id));
      toast.success('Photo supprimée');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="h2 text-anthracite-950">Galerie photos</h1>
          <p className="text-muted-foreground mt-1">{items.length} photo{items.length > 1 ? 's' : ''}</p>
        </div>
        <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (o) setForm(f => ({ ...f, category: activeTab })); }}>
          <DialogTrigger asChild>
            <Button data-testid="admin-add-photo"><Plus className="h-4 w-4" /> Ajouter une photo</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle photo</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label>URL de l'image *</Label><Input type="url" required value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." /></div>
              <div><Label>Titre</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div>
                <Label>Catégorie *</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ordre d'affichage</Label><Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} /></div>
              {form.url && (
                <div className="aspect-[4/3] rounded-lg overflow-hidden border bg-anthracite-100">
                  <img src={form.url} alt="Prévisualisation" className="w-full h-full object-cover" />
                </div>
              )}
              <Button type="submit" className="w-full">Ajouter</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-700" /></div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full overflow-x-auto justify-start bg-anthracite-100/60">
            {CATEGORIES.map(([k, l]) => <TabsTrigger key={k} value={k}>{l}</TabsTrigger>)}
          </TabsList>
          {CATEGORIES.map(([k]) => {
            const filtered = items.filter(i => i.category === k);
            return (
              <TabsContent key={k} value={k} className="mt-6">
                {filtered.length === 0 ? (
                  <p className="text-muted-foreground text-center py-12">Aucune photo dans cette catégorie.</p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map(p => (
                      <figure key={p.id} className="card-base overflow-hidden">
                        <div className="aspect-[4/3] bg-anthracite-100">
                          <img src={p.url} alt={p.title || ''} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <figcaption className="p-3 flex items-center justify-between gap-2">
                          <span className="text-sm truncate">{p.title || 'Sans titre'}</span>
                          <Button variant="outline" size="sm" onClick={() => remove(p.id)} className="text-destructive border-destructive/30 hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}
